import { httpsCallable } from 'firebase/functions';
import { ref, getBytes } from 'firebase/storage';
import { storage, functions } from '../firebase';
import logoFlyerSrc from '../assets/logo-flyer.png?url';

const imagenFlyerCallable = httpsCallable(functions, 'imagenFlyerDataUrl');

/** URL del logo empaquetado con Vite (siempre disponible en el flyer). */
export { logoFlyerSrc };

let logoDataUrlCache = null;

export function storagePathFromDownloadUrl(url) {
  if (!url || typeof url !== 'string') return null;
  try {
    const match = url.match(/\/o\/([^?]+)/);
    return match ? decodeURIComponent(match[1]) : null;
  } catch {
    return null;
  }
}

function mimeFromPath(path) {
  const ext = (path || '').split('.').pop()?.toLowerCase();
  if (ext === 'png') return 'image/png';
  if (ext === 'webp') return 'image/webp';
  if (ext === 'bmp') return 'image/bmp';
  if (ext === 'gif') return 'image/gif';
  return 'image/jpeg';
}

function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

export async function cargarAssetLocalDataUrl(ruta) {
  try {
    const res = await fetch(ruta, { cache: 'force-cache' });
    if (!res.ok) return null;
    return await blobToDataUrl(await res.blob());
  } catch {
    return null;
  }
}

/** Logo del flyer como data URL (mejor para html2canvas). */
export async function obtenerLogoFlyerDataUrl() {
  if (logoDataUrlCache) return logoDataUrlCache;
  try {
    const res = await fetch(logoFlyerSrc);
    if (!res.ok) return null;
    logoDataUrlCache = await blobToDataUrl(await res.blob());
    return logoDataUrlCache;
  } catch (e) {
    console.warn('Logo flyer:', e?.message || e);
    return null;
  }
}

/** Cloud Function: lee Storage en servidor (sin CORS en el cliente). */
async function imagenViaCloudFunction(path) {
  if (!path) return null;
  try {
    const { data } = await imagenFlyerCallable({ path });
    return data?.dataUrl || null;
  } catch (e) {
    console.warn('Flyer (función):', path, e?.code || e?.message);
    return null;
  }
}

/** Respaldo directo por SDK (requiere CORS en el bucket). */
async function imagenViaGetBytes(path) {
  if (!path) return null;
  try {
    const bytes = await getBytes(ref(storage, path));
    return await blobToDataUrl(new Blob([bytes], { type: mimeFromPath(path) }));
  } catch (e) {
    console.warn('Flyer (getBytes):', path, e?.code || e?.message);
    return null;
  }
}

export async function imagenStorageADataUrl(url, pathExplicito) {
  const path = pathExplicito || storagePathFromDownloadUrl(url);
  if (!path) return null;
  return (await imagenViaCloudFunction(path)) || (await imagenViaGetBytes(path));
}

async function resolverImagenPremio(rifa, index) {
  const imgs = Array.isArray(rifa.imagenesPremios) ? rifa.imagenesPremios : [];
  const entry = imgs[index];
  if (entry?.path || entry?.url) {
    const data = await imagenStorageADataUrl(entry.url, entry.path);
    if (data) return data;
  }
  if (index === 0 && rifa.imagenUrl) {
    return await imagenStorageADataUrl(rifa.imagenUrl, null);
  }
  return null;
}

export async function prepararRifaParaFlyer(rifa) {
  const premiosCount = Array.isArray(rifa.premios)
    ? rifa.premios.length
    : rifa.premio
      ? rifa.premio.split(',').length
      : 1;

  const [imagenesPremiosDataUrls, logoDataUrl] = await Promise.all([
    Promise.all(
      Array.from({ length: Math.min(premiosCount, 3) }, (_, i) => resolverImagenPremio(rifa, i))
    ),
    obtenerLogoFlyerDataUrl(),
  ]);

  return {
    ...rifa,
    imagenesPremiosDataUrls,
    logoDataUrl: logoDataUrl || null,
    logoSrc: logoDataUrl || logoFlyerSrc,
  };
}
